import { useMemo, useState, type FormEvent } from "react";
import ReviewCard from "@/components/feature/ReviewCard";
import StarRating from "@/components/base/StarRating";
import { reviewsByEpisode, type Review } from "@/mocks/reviews";

interface EpisodeCommentsProps {
  episodeId: string;
}

export default function EpisodeComments({ episodeId }: EpisodeCommentsProps) {
  const initial = useMemo<Review[]>(() => reviewsByEpisode(episodeId), [episodeId]);
  const [list, setList] = useState<Review[]>(initial);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !content.trim()) {
      setError("请填写昵称和点评内容后再提交。");
      return;
    }
    setError("");
    const newReview: Review = {
      id: `local-${Date.now()}`,
      episodeId,
      author: name.trim(),
      avatarSeed: name.trim().charAt(0),
      rating,
      date: new Date().toISOString().slice(0, 10),
      content: content.trim(),
      likes: 0,
      featured: false,
    };
    setList((prev) => [newReview, ...prev]);
    setName("");
    setRating(5);
    setContent("");
    setToast("点评已发表（前端演示，仅当前页面可见）");
    window.setTimeout(() => setToast(""), 3000);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          观众点评
        </h2>
        <span className="text-sm text-foreground-500">（{list.length} 条）</span>
      </div>

      <form
        onSubmit={submit}
        className="rounded-lg bg-background-100 border border-background-200 p-5 mb-6"
      >
        <p className="text-xs text-foreground-500 mb-4 flex items-center gap-1.5">
          <i className="ri-information-line text-accent-400" />
          评论功能为本站前端演示，提交后仅当前页面可见，不会上传至任何服务器。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="flex items-center h-11 px-3 rounded-md bg-background-50 border border-background-200 focus-within:border-primary-500 transition-colors">
            <i className="ri-user-line text-foreground-500" />
            <input
              name="author"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="你的昵称"
              className="flex-1 bg-transparent outline-none border-none text-sm text-foreground-900 placeholder:text-foreground-500 px-2"
            />
          </div>
          <div className="flex items-center h-11 px-3 rounded-md bg-background-50 border border-background-200">
            <span className="text-sm text-foreground-600 mr-2 whitespace-nowrap">评分</span>
            <select
              name="rating"
              value={rating}
              onChange={(event) => setRating(Number(event.target.value))}
              className="bg-transparent outline-none border-none text-sm text-foreground-900 cursor-pointer"
            >
              {[5, 4, 3, 2, 1].map((value) => (
                <option key={value} value={value} className="bg-background-100">
                  {value} 星
                </option>
              ))}
            </select>
          </div>
        </div>
        <textarea
          name="content"
          value={content}
          maxLength={500}
          onChange={(event) => setContent(event.target.value)}
          placeholder="说说你对这一集的看法（最多 500 字）"
          rows={3}
          className="mt-3 w-full rounded-md bg-background-50 border border-background-200 focus:border-primary-500 outline-none text-sm text-foreground-900 placeholder:text-foreground-500 p-3 resize-none transition-colors"
        />
        <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="text-xs text-foreground-500">{content.length} / 500</span>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {error && <span className="text-xs text-primary-300">{error}</span>}
            <button
              type="submit"
              className="flex-1 sm:flex-none h-10 px-6 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              发表点评
            </button>
          </div>
        </div>
        {toast && (
          <p className="mt-3 text-xs text-accent-400 flex items-center gap-1.5 animate-fade-in">
            <i className="ri-check-line" />
            {toast}
          </p>
        )}
      </form>

      {list.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 rounded-lg border border-dashed border-background-300 bg-background-100/50">
          <StarRating rating={0} size="lg" />
          <p className="font-heading text-base text-foreground-800 mt-3">
            暂时无人点评，欢迎发表看法
          </p>
        </div>
      )}
    </div>
  );
}